// Playlist item types (PlaylistItemType on the server).
export const ITEM_TYPES = { media: 0, webPage: 1, text: 2, clock: 3 };

const KNOWN_TYPES = new Set(Object.values(ITEM_TYPES));

// Types that load from the internet while playing: skipped while the screen is offline.
const NEEDS_NETWORK = new Set([ITEM_TYPES.webPage]);

// Upper bound for a natural-length video whose duration is unknown; its `ended` event normally
// advances sooner.
export const UNKNOWN_VIDEO_SECONDS = 600;

export const itemType = (item) => item?.type ?? ITEM_TYPES.media;

export const isVideoItem = (item) => {
  if (itemType(item) !== ITEM_TYPES.media) return false;
  const file = item?.file;
  if (file?.mediaType) return file.mediaType === 'video';
  if (file?.contentType) return file.contentType.startsWith('video');
  try {
    return (
      JSON.parse(file?.info || '{}').ContentType?.includes('video') ?? false
    );
  } catch {
    return false;
  }
};

// "2026-10-14" from a date or date-time string; null when absent.
const dayOf = (value) => (value ? String(value).slice(0, 10) : null);

/**
 * Whether an item can play now: a type this player knows, a file for media, the network for
 * online-only types, and today (screen-local "YYYY-MM-DD") inside its optional date window.
 */
export const isPlayable = (item, { online = true, dayKey } = {}) => {
  const type = itemType(item);
  if (!KNOWN_TYPES.has(type)) return false;
  if (type === ITEM_TYPES.media && !item?.file?.fileName) return false;
  if (!online && NEEDS_NETWORK.has(type)) return false;
  if (dayKey) {
    const from = dayOf(item?.validFrom);
    const to = dayOf(item?.validTo);
    if (from && dayKey < from) return false;
    if (to && dayKey > to) return false;
  }
  return true;
};

export const playableItems = (playlist, context) =>
  (playlist?.playlistContents || []).filter((item) =>
    isPlayable(item, context),
  );

/**
 * Seconds an item stays on screen. A positive item duration wins; otherwise a video plays to its
 * natural length (from upload metadata, else until it ends) and anything else uses the playlist's
 * default duration.
 */
export const itemSeconds = (item, playlist) => {
  if (item?.duration > 0) return item.duration;
  if (isVideoItem(item)) {
    const natural = item?.file?.durationSeconds;
    return natural > 0 ? Math.ceil(natural) : UNKNOWN_VIDEO_SECONDS;
  }
  return playlist?.duration > 0 ? playlist.duration : 10;
};

// Plays to its end (advances on `ended`) rather than for a fixed time.
export const playsToEnd = (item) => isVideoItem(item) && !(item?.duration > 0);

// What the console's live monitor shows for an item.
export const itemLabel = (item) => {
  switch (itemType(item)) {
    case ITEM_TYPES.webPage:
      return item?.settings?.url || 'web page';
    case ITEM_TYPES.text:
      return `text: ${(item?.settings?.text || '').slice(0, 40)}`;
    case ITEM_TYPES.clock:
      return 'clock';
    default:
      return item?.file?.fileName;
  }
};
